// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Drag- and keyboard-resize for a panel docked to the viewport's right edge.
 *
 * Extracted verbatim from `useAskHeliosResize`, which is now a thin wrapper:
 * the Asset Details panel needs the same gesture against its own width, and the
 * only things that differ between the two are the CSS variable, the bounds and
 * the accessible label.
 *
 * The panel is pinned to the right edge, so its width is simply the distance
 * from the pointer to that edge (`window.innerWidth - clientX`). The handle sits
 * on the panel's left edge; dragging it left widens the panel.
 *
 * Following the same rationale as `useStageTransform`, a live drag mutates the
 * CSS custom property directly rather than routing every `pointermove` through
 * React state — so the panel body (and anything else reading the variable)
 * tracks the drag without re-rendering. The committed width is written to the
 * store only on release, where it is clamped and persisted.
 */

import { useCallback, useEffect, useRef } from 'react';

/** Keyboard nudge per Arrow press, in px. */
const KEYBOARD_STEP = 24;

export interface UseDockedPanelResizeOptions {
  /** CSS custom property the panel's width is read from, e.g. `--ask-helios-width`. */
  widthVar: string;
  /** Committed width from the store. */
  width: number;
  /** Commits (and persists) a released width. Expected to clamp again. */
  setWidth: (_px: number) => void;
  minWidth: number;
  maxWidth: number;
  /**
   * Width of a sibling dock between this panel and the viewport's right edge,
   * in px. `0` when this panel is the outermost one.
   *
   * The Asset Details panel shifts left by the Ask Helios width when both are
   * open, so with a sibling the panel's right edge is no longer the viewport's.
   * Without this the drag reads every width `offsetRight` px too wide, and the
   * clamp lets the two docks total more than the screen — on a 1,440 px display
   * their two maxima come to 1,600, which pushes this panel's left edge, and
   * the resize handle sitting on it, off the screen entirely.
   */
  offsetRight?: number;
  /** Accessible name for the separator. */
  label: string;
}

export interface DockedPanelHandleProps {
  onPointerDown: (_event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerMove: (_event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerUp: (_event: React.PointerEvent<HTMLDivElement>) => void;
  onPointerCancel: (_event: React.PointerEvent<HTMLDivElement>) => void;
  onKeyDown: (_event: React.KeyboardEvent<HTMLDivElement>) => void;
  role: 'separator';
  'aria-orientation': 'vertical';
  'aria-label': string;
  'aria-valuenow': number;
  'aria-valuemin': number;
  'aria-valuemax': number;
  tabIndex: number;
}

export interface UseDockedPanelResizeResult {
  /** Spread onto the left-edge resize handle element. */
  handleProps: DockedPanelHandleProps;
}

export const useDockedPanelResize = ({
  widthVar,
  width,
  setWidth,
  minWidth,
  maxWidth,
  offsetRight = 0,
  label,
}: UseDockedPanelResizeOptions): UseDockedPanelResizeResult => {
  const liveWidth = useRef(width);
  const dragging = useRef(false);
  const pointerId = useRef<number | null>(null);

  const setWidthVar = useCallback(
    (px: number): void => {
      if (typeof document === 'undefined') return;
      document.documentElement.style.setProperty(widthVar, `${px}px`);
    },
    [widthVar]
  );

  const clampWidth = useCallback(
    (px: number): number => {
      const available =
        typeof window !== 'undefined' && window.innerWidth > 0
          ? window.innerWidth - offsetRight
          : maxWidth;
      const max = Math.min(maxWidth, available);
      return Math.max(minWidth, Math.min(max, Math.round(px)));
    },
    [maxWidth, minWidth, offsetRight]
  );

  useEffect(() => {
    liveWidth.current = width;
    setWidthVar(width);
  }, [setWidthVar, width]);

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    dragging.current = true;
    pointerId.current = event.pointerId;
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Some browsers reject capture for already-released pointers.
      }
    }
    event.preventDefault();
  }, []);

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging.current) return;
      if (pointerId.current !== null && event.pointerId !== pointerId.current) return;
      const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 0;
      const next = clampWidth(viewportWidth - offsetRight - event.clientX);
      liveWidth.current = next;
      setWidthVar(next);
      event.currentTarget.setAttribute('aria-valuenow', String(next));
    },
    [clampWidth, offsetRight, setWidthVar]
  );

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!dragging.current) return;
      if (pointerId.current !== null && event.pointerId !== pointerId.current) return;
      dragging.current = false;
      pointerId.current = null;
      const target = event.currentTarget;
      if (typeof target.releasePointerCapture === 'function') {
        try {
          target.releasePointerCapture(event.pointerId);
        } catch {
          // Pointer already released.
        }
      }
      setWidth(liveWidth.current);
    },
    [setWidth]
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      let next: number | null = null;
      if (event.key === 'ArrowLeft') next = liveWidth.current + KEYBOARD_STEP;
      else if (event.key === 'ArrowRight') next = liveWidth.current - KEYBOARD_STEP;
      if (next === null) return;
      event.preventDefault();
      setWidth(next);
    },
    [setWidth]
  );

  return {
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown,
      role: 'separator',
      'aria-orientation': 'vertical',
      'aria-label': label,
      'aria-valuenow': width,
      'aria-valuemin': minWidth,
      'aria-valuemax': maxWidth,
      tabIndex: 0,
    },
  };
};
